import { z } from "zod";

/**
 * AUDIT / KULLANIM / ABONELİK AI İÇGÖRÜLERİ (CLOS-291 `[195]` · CLOS-1025/1026/1027).
 *
 * ### ⚠ AI NE AUDIT'E NE SAYACA DOKUNUYOR
 *
 * Kartın kapsam sınırı: *"AI audit olayı oluşturmaz/değiştirmez/silmez ... Kullanım
 * sayacını, lisans durumunu, planı, ek hakkı, ödemeyi, iadeyi, iptali ... uygulamaz."*
 * Şemada bir "uygula", "kapat" ya da "yükselt" alanı yok; `suggestedStep` kullanıcının
 * kendi eliyle gideceği kanonik akışı işaret eden bir CÜMLE.
 *
 * ### ⚠ SAYILAR AI'DAN DEĞİL, KANONİK MOTORDAN
 *
 * Kart: *"AI kendi sayacını veya limiti hesap kaynağı yapmaz."* Bu yüzden çıktıda bir
 * "kullanım yüzdesi" ya da "kalan kota" alanı YOK: kullanım ve limit sunucunun
 * projeksiyonundan geliyor ve ekranda oradan gösteriliyor. Modelin işi o sayıları
 * YORUMLAMAK, üretmek değil.
 */

export const AI_OPS_INSIGHT_CATEGORIES = [
  "audit_pattern",
  "usage_trend",
  "limit_risk",
  "unused_capacity",
  "plan_fit",
] as const;
export type AiOpsInsightCategory = (typeof AI_OPS_INSIGHT_CATEGORIES)[number];

export const AI_OPS_INSIGHT_CATEGORY_NAMES: Record<AiOpsInsightCategory, string> = {
  audit_pattern: "Olay örüntüsü",
  usage_trend: "Kullanım eğilimi",
  limit_risk: "Limit riski",
  unused_capacity: "Kullanılmayan kapasite",
  plan_fit: "Paket uyumu",
};

export const AI_OPS_INSIGHT_PRIORITIES = ["high", "medium", "low"] as const;
export type AiOpsInsightPriority = (typeof AI_OPS_INSIGHT_PRIORITIES)[number];

export const AI_OPS_INSIGHT_PRIORITY_NAMES: Record<AiOpsInsightPriority, string> = {
  high: "Önce bunu inceleyin",
  medium: "İncelenmeli",
  low: "Bilgi amaçlı",
};

/** ⚠ EKRANDA ZORUNLU: içgörü bir ihlal kararı ya da ticari işlem değil. */
export const AI_OPS_INSIGHT_ADVISORY =
  "Bu içgörüler bir güvenlik ihlali kararı, fatura işlemi ya da plan değişikliği değildir; " +
  "hiçbiri kendiliğinden uygulanmaz.";

export const AI_OPS_INSIGHT_EMPTY_MESSAGE =
  "Bu dönemde öne çıkan bir eğilim ya da örüntü çıkmadı. Bu, incelenecek bir şey olmadığı " +
  "anlamına gelmez.";

export const aiOpsInsightSchema = z.strictObject({
  category: z.enum(AI_OPS_INSIGHT_CATEGORIES),
  priority: z.enum(AI_OPS_INSIGHT_PRIORITIES),
  confidence: z.enum(["high", "medium", "low"]),
  title: z.string().min(1).max(200),
  rationale: z.string().min(1).max(1500),
  /** Hangi veri dönemine bakıldığı — belirsizliğin sınırı (kartın kriteri). */
  period: z.string().min(1).max(120),
  /** ⚠ Kullanıcının KENDİ yapacağı inceleme/işlem adımı — bir komut değil. */
  suggestedStep: z.string().min(1).max(400),
  /**
   * ⚠ Audit örüntüsü içgörüleri hangi OLAY TÜRLERİNE dayanıyor.
   */
  actions: z.array(z.string().max(120)).max(20).default([]),
  /**
   * ⚠ HANGİ TEKİL OLAYLARA dayanıyor — özetteki SIRA NUMARALARI (`#3`), olay UUID'si
   * DEĞİL. Kartın kriteri içgörünün *"kaynak audit olay kimlikleri ve correlation
   * zincirine bağlanmasını"* istiyor; bağı sunucu kuruyor: modelin verdiği sıra numarası
   * özetin satırıyla eşleştirilip gerçek olay kimliğine ve correlation kimliğine
   * çözülüyor. Modele ham UUID göndermemek CLOS-208'in kuralı (kimlik sağlayıcıya
   * gitmez); bağın KENDİSİ ise sunucuda kuruluyor ve doğrulanabiliyor.
   */
  sourceRefs: z.array(z.number().int().min(1).max(500)).max(20).default([]),
});
export type AiOpsInsight = z.infer<typeof aiOpsInsightSchema>;

export const aiOpsInsightOutputSchema = z.strictObject({
  insights: z.array(aiOpsInsightSchema).max(20),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(10).default([]),
});
export type AiOpsInsightOutput = z.infer<typeof aiOpsInsightOutputSchema>;

/**
 * ⚠ MODELİN SAYDIĞI OLAY TÜRÜ GERÇEKTEN GÖRÜLDÜ MÜ. Sunucu her `actions` girdisini
 * projeksiyona giren olay türleriyle karşılaştırıyor; eşleşmeyen tür `unknownActions`'a
 * düşüyor ve ekran o içgörüyü "kaynağı doğrulanamadı" diye işaretliyor. Kartın *"tarihsel
 * aktör, sonuç veya önceki-yeni durum AI tarafından YENİDEN YAZILMAZ"* kriterinin
 * ölçülebilir hâli.
 */
export type AiOpsInsightView = AiOpsInsight & {
  unknownActions: string[];
  /**
   * ⚠ Sunucunun çözdüğü KAYNAK ZİNCİRİ: içgörünün dayandığı tekil olayların kanonik
   * kimlikleri ve correlation kimlikleri. Bunlar modelin ürettiği değil, sunucunun
   * özetteki sıra numarasından çözdüğü değerler — model bir kimlik UYDURAMAZ, çünkü
   * kimliği hiç görmüyor.
   */
  sources: AiOpsInsightSource[];
  /** ⚠ Özette karşılığı olmayan sıra numarası: model olmayan bir satıra atıf yaptı. */
  invalidRefs: number[];
  /**
   * ⚠ AI TİCARİ BİR İŞLEM YAPILDIĞINI İMA ETTİ Mİ (CLOS-1090 *"no-financial-action"*).
   * İçgörü bir satın alma/yükseltme bildirmemeli; işaretleniyor, silinmiyor.
   */
  certaintyFlagged: boolean;
};

export type AiOpsInsightSource = {
  eventId: string;
  correlationId: string | null;
  action: string;
  occurredAt: string;
};

export type AiOpsInsightRunSummary = {
  id: string;
  status: "completed" | "failed";
  insights: AiOpsInsightView[];
  reviewNeeded: string[];
  errorCode: "provider_error" | "invalid_output" | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  /** Kaç audit olayı ve kaç sayaç satırı özetlendi — kapsamın ölçüsü. */
  auditCount: number;
  createdAt: string;
};

export const AI_OPS_INSIGHT_ERROR_MESSAGES: Record<"provider_error" | "invalid_output", string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; içgörü üretilemedi ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

export const AI_OPS_INSIGHT_INVALID_REF_WARNING =
  "Bu içgörü, incelenen özette bulunmayan bir kayda atıf yapıyor; kaynak zinciri eksik.";

export const AI_OPS_INSIGHT_UNKNOWN_ACTION_WARNING =
  "Bu içgörünün dayandığı olay türlerinden bazıları incelenen dönemde görülmedi; " +
  "doğrulamadan işlem yapmayın.";
